import { createFileRoute, notFound, redirect } from '@tanstack/react-router';

import { flagBasedComponent } from '@mint-lib/routing-context';
import { buildGuards, hasPermission } from '@mint-lib/routing-guards';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import { socket } from '@mint-sdk/api-media-plan/socket';
import { io } from 'socket.io-client';
import { z } from 'zod';

import { Layout } from '@myn/planning/routes';
import { Layout as LayoutV2 } from '@myn/planning_v2/routes';

const guard = buildGuards(hasPermission('campaignPlanningFeature:view'));

const port = globalThis.location.port;
const isSecure = port === '' || port === '443';
const protocol = isSecure ? 'wss' : 'ws';
const portSuffix = port === '443' ? '' : `:${port}`;
const WS_URL = `${protocol}://${globalThis.location.hostname}${portSuffix}`;

export const PanelTab = {
  AddRow: 'AddRow',
  Recommendations: 'Recommendations',
  Co2: 'Co2',
  Versions: 'Versions',
} as const;

const establishSocketConnection = async (accessToken: string) => {
  const rawSocket = io(
    `${WS_URL}/api/planning-service/socket-io/planning-updates/`,
    {
      transports: ['websocket'],
      path: '/api/planning-service/socket-io',
      query: {
        authorization: encodeURIComponent(accessToken),
      },
    },
  );

  socket.provideClient(rawSocket);
};

export const Route = createFileRoute(
  '/_secured/campaign/$campaignId/planning/buying-plan',
)({
  component: flagBasedComponent('planningV2', '@mint/arm', LayoutV2, Layout),

  validateSearch: z.object({
    mediaplan: z.number().optional(),
    tab: z.enum(PanelTab).optional(),
    row: z.number().optional(),

    // planning_v2
    activity: z.string().optional(),
    flight: z.string().optional(),
    mediaPlan: z.string().optional(),
    flightVersionId: z.string().optional(),
  }),
  async beforeLoad({ context, search, location }) {
    const guardsContext = await context.getGuardContext(
      await context.getInstance('flagsmith', '@myn/secured'),
    );
    if (!guard(guardsContext)) {
      throw notFound();
    }

    // The activity level is hidden for user, so it must not stay reachable through the
    // URL. Dropping `activity` falls back to `flight` when present, otherwise to the media
    // plan - see `useChosenLevel` in @myn/planning_v2.
    if (
      search.activity &&
      (await context.isFlagEnabled('@mint/arm', 'disableActivityLevel'))
    ) {
      const { activity, ...restSearch } = search;

      throw redirect({
        to: location.pathname,
        search: restSearch,
        replace: true,
      });
    }
  },
  loader: async ({ context, params }) => {
    const i18nOld = await context.getInstance('i18n', '@mint/routing');
    // IDK why you created a new namespace for the planning module TBH
    const i18n = await context.getInstance('i18n', '@mint/routing');

    const isPlanningV2 = await context.isFlagEnabled('@mint/arm', 'planningV2');

    await Promise.all([
      context.getInstance(
        'i18n',
        isPlanningV2 ? '@myn/planning_v2' : '@myn/planning',
      ),
      context.getInstance('i18n', '@mint-lib/formatting-hooks'),
      context.getInstance(
        'i18n',
        isPlanningV2
          ? '@mint-lib/approval-flow-circuit_v2'
          : '@mint-lib/approval-flow-circuit',
      ),
    ]);

    if (isPlanningV2) {
      const accessToken = (await context.getInstance('auth')).getAccessToken();

      if (!accessToken) {
        throw new Error('User is not authenticated');
      }

      await context.getInstance('i18n', '@mint-lib/ag-grid');
      await establishSocketConnection(accessToken);
      context.prefetchQuery(
        MediaPlan.getPreviewActivityListQueryOptions({
          params: {
            mediaPlanId: params.campaignId,
          },
        }),
      );

      await context
        .prefetchQuery({
          ...MediaPlan.getMetadataQueryOptions({
            urlParams: {
              mediaPlanId: params.campaignId,
            },
          }),
        })
        .catch((err) => {
          const errorCode = err.response?.status ?? 0;
          // 403 Forbidden, 404 Not Found
          // 422 Unprocessable Entity it's when id isn't integer
          const nonAllowedErrorCodes = new Set([403, 404, 422]);
          if (nonAllowedErrorCodes.has(errorCode)) {
            throw notFound();
          }

          throw err;
        });
    }

    const title = isPlanningV2
      ? i18n.t('Media Plan - {{campaignId}} | MINT', {
          campaignId: params.campaignId,
        })
      : i18nOld.t('Buying Plan - {{campaignId}} | MINT', {
          campaignId: params.campaignId,
        });

    return { title };
  },
  onLeave: () => {
    socket.getClient().disconnect?.();
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData?.title,
      },
    ],
  }),
});
